import { useRef, useState } from 'react';

import { Delete } from 'ui/icons';
import { colors } from 'ui/theme';
import { useClickOutside } from 'utils/hooks/useClickOutside';
import { useDebouncedEffect } from 'utils/hooks/useDebouncedEffect';

import {
  NoteWrapper,
  StyledResizableTextArea,
  TrashButton,
  StyledNoteCorner,
  StyledCharacterCount,
} from './EditorNote.styles';

type EditorNoteProps = {
  /** text in note */
  noteText: string | null;
  /** update note text */
  updateNoteText: (noteText: string | null) => void;
  /** function to deleteNote */
  deleteNote: () => void;
  /** is tile editor read only */
  isReadOnly?: boolean;
};

const content = {
  notePlaceholder: 'Write a note...',
};

export const EditorNote = ({
  noteText,
  updateNoteText,
  deleteNote,
  isReadOnly = false,
  ...props
}: EditorNoteProps) => {
  // used to allow the text area to resize
  const [isCollapsed, setIsCollapsed] = useState(true);
  const noteMinHeight = isCollapsed ? 108 : 140;

  // Debounces updating noteText
  const [value, setValue] = useState<string | null>(noteText);
  useDebouncedEffect(
    () => {
      updateNoteText(value);
    },
    [value],
    { debounceTimeout: 250, skipEffectOnMount: true },
  );
  const handleChange = (val: string) => {
    if (isCollapsed) {
      setIsCollapsed(false);
    }
    if (val.length <= 500) {
      setValue(val);
    }
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, () => {
    setIsCollapsed(true);
  });

  return (
    <>
      <NoteWrapper $collapsed={isCollapsed} $minHeight={noteMinHeight} ref={wrapperRef}>
        <StyledResizableTextArea
          data-test={`editor-note${isCollapsed ? '-collapsed' : ''}`}
          value={value || ''}
          onChange={handleChange}
          onClick={() => (isReadOnly ? undefined : setIsCollapsed(false))}
          maxLength={500}
          placeholder={content.notePlaceholder}
          isDisabled={isReadOnly}
          {...props}
        />
        {!isCollapsed && (
          <StyledCharacterCount>{`${value ? value.length : 0}/500`}</StyledCharacterCount>
        )}
        <StyledNoteCorner
          height={32}
          width={32}
          stroke={isCollapsed ? colors.mustard50 : colors.mustard100}
        />
      </NoteWrapper>
      {!isReadOnly && (
        <TrashButton
          data-test="editor-note-delete-button"
          onClick={() => {
            updateNoteText(null);
            deleteNote();
          }}
        >
          <Delete height={16} width={16} color={colors.white} />
        </TrashButton>
      )}
    </>
  );
};
